import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../Components/AuthContext';

// Define the inactivity limit in milliseconds (20 minutes)
const INACTIVITY_LIMIT = 20 * 60 * 1000; // 20 min
const WARNING_TIME = 60 * 1000; // 60 seconds before logout

const AutoLogoutWrapper = ({ children }) => {
    const navigate = useNavigate();
    const timerId = useRef(null);
    const warningTimerId = useRef(null); 

    const { logout } = useAuth();

    // Function to reset the inactivity timer
    const resetTimer = () => {
        // Clear any existing timers to avoid multiple overlapping timers
        clearTimeout(timerId.current);
        clearTimeout(warningTimerId.current);

        // Set a warning timer to alert the user before logout
        warningTimerId.current = setTimeout(() => {
            window.alert(
                "⚠️ Heads up! You've been inactive for a while. If you don't move or click something soon, we'll log you out to keep things secure. 😊"
            );
        }, INACTIVITY_LIMIT - WARNING_TIME);

        // Set the actual logout timer
        timerId.current = setTimeout(() => {
            window.alert(
                "👋 You've been logged out due to inactivity to keep MD-Sensei secure. Come back soon!"
            );
            logout(); // Call the logout function from the AuthContext
        }, INACTIVITY_LIMIT);
    };

    // useEffect to initialize activity listeners and manage timers
    useEffect(() => {
        // events that we are listening to for user activity
        const events = ['mousemove', 'keydown', 'click', 'scroll'];

        // On activity, reset timer
        const handleActivity = () => {
            resetTimer();
        };

        // Add event listeners for each activity type
        events.forEach((event) =>
            window.addEventListener(event, handleActivity)
        );

        // Start the initial timer when the component mounts
        resetTimer();

        // Cleanup function to remove event listeners and clear timers
        return () => {
            clearTimeout(timerId.current); // Clear the logout timer
            clearTimeout(warningTimerId.current); // Clear the warning timer
            events.forEach((event) =>
                window.removeEventListener(event, handleActivity)
            ); // Remove event listeners
        };
    }, []); 

    // Render the children components wrapped by this AutoLogoutWrapper
    return <>{children}</>;
};

export default AutoLogoutWrapper;
